import { Dispatch, SetStateAction, useMemo } from "react";
import { RuleBaseGraph } from "shared/app/RuleBaseGraph";
import { Card } from "shared/components/Card";
import { ApplicationStatus, DecisionAction } from "shared/types";
import { cx } from "shared/utils";
import { Reasons } from "types/application";
import { RecommendationButton } from "views/applications-id/components/RecommendationButton";
import { getDecisionRecommendationCardText } from "views/applications-id/helpers";

const getDecisionActionStyles = (recommendation?: DecisionAction) => {
  switch (recommendation) {
    case DecisionAction.Approve:
      return {
        bg: "bg-success-50",
        text: "text-success-700"
      };
    case DecisionAction.Decline:
      return {
        bg: "bg-danger-50",
        text: "text-danger-500"
      };

    default:
      return {
        bg: "bg-warning-50",
        text: "text-warning-700"
      };
  }
};

export const RuleBasedRecommendation = ({
  recommendation,
  autoApproved,
  setShowExplainRecommendations,
  setShowDecisionFlowModal,
  canMakeDecision,
  ruleReasons,
  applicationStatus
}: {
  autoApproved: boolean;
  ruleReasons?: Reasons[];
  applicationStatus: ApplicationStatus;
  recommendation?: DecisionAction;
  canMakeDecision: boolean;
  setShowDecisionFlowModal: Dispatch<SetStateAction<boolean>>;
  setShowExplainRecommendations: Dispatch<SetStateAction<boolean>>;
}) => {
  const styles = useMemo(() => {
    return getDecisionActionStyles(recommendation);
  }, [recommendation]);

  return (
    <Card className="flex flex-col max-w-[300px] w-full shrink max-h-[340px] h-full">
      <div
        className={cx(
          "p-6 w-full flex flex-col items-center justify-center rounded-t-lg gap-4 flex-grow",
          styles.bg
        )}
      >
        <RuleBaseGraph className="size-12" recommendation={recommendation} />
        <p className={cx("text-base font-bold text-center", styles.text)}>
          {getDecisionRecommendationCardText({
            autoApproved: autoApproved,
            recommendation: recommendation
          })}
        </p>
      </div>
      <div className="p-6 w-full mt-auto px-6 border-t border-alpha-40">
        <RecommendationButton
          onClickMakeDecision={() => setShowDecisionFlowModal(true)}
          onClickExplainRecommendation={() =>
            setShowExplainRecommendations(true)
          }
          canMakeDecision={canMakeDecision}
          recommendation={recommendation}
          hasReasons={Boolean(ruleReasons?.length)}
          status={applicationStatus}
          autoApproved={autoApproved}
        />
      </div>
    </Card>
  );
};
